import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Fingerprint } from "lucide-react";
import { startAuthentication } from "@simplewebauthn/browser";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { useAuth, STAFF } from "@/lib/auth";
import { api, errCode } from "@/lib/api";
import { Field } from "@/pages/Checkout";
import { LOGIN, REGISTER } from "@/constants/testIds";

const Shell = ({ title, sub, children }) => (
  <div className="container-x py-12 sm:py-20 flex justify-center"><div className="w-full max-w-md card p-6 sm:p-8 space-y-6 rise">
    <div className="space-y-1"><h1 className="text-2xl font-bold">{title}</h1><p className="muted text-sm">{sub}</p></div>{children}</div></div>
);

const GoogleBtn = () => {
  const { t } = useI18n();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const go = () => { window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/account")}`; };
  return <button type="button" data-testid="google-login-btn" onClick={go} className="btn-ghost w-full">
    <svg className="w-5 h-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
    {t("auth.google")}</button>;
};

export function Login() {
  const { t } = useI18n();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const done = (u) => { setUser(u); nav(sp.get("next") || (STAFF.includes(u.role) ? "/admin" : "/account"), { replace: true }); };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/login", f); await api.get("/auth/me").then((r) => done(r.data)).catch(() => done(data)); }
    catch (e2) { toast.error(t(`errors.${errCode(e2)}`)); } finally { setBusy(false); }
  };
  const passkey = async () => {
    try {
      const { data } = await api.post("/auth/passkey/auth/options", { email: f.email || null });
      const cred = await startAuthentication({ optionsJSON: data.options });
      await api.post(`/auth/passkey/auth/verify/${data.challenge_id}`, { credential: cred, purpose: "login" });
      const me = await api.get("/auth/me"); done(me.data);
    } catch (e) { toast.error(t(`errors.${e?.response ? errCode(e) : "passkey_failed"}`)); }
  };
  return (
    <Shell title={t("auth.login_title")} sub={t("auth.login_sub")}>
      <button type="button" data-testid="passkey-login-btn" onClick={passkey} className="btn-dark w-full"><Fingerprint className="w-5 h-5" />{t("auth.passkey_login")}</button>
      <GoogleBtn />
      <div className="flex items-center gap-3 text-xs muted"><span className="flex-1 h-px bg-[#E5E7EB]" />{t("auth.or_email")}<span className="flex-1 h-px bg-[#E5E7EB]" /></div>
      <form onSubmit={submit} className="space-y-4">
        <Field label={t("checkout.email")} testid={LOGIN.emailInput} type="email" required autoComplete="username webauthn" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field label={t("auth.password")} testid={LOGIN.passwordInput} type="password" required autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <button data-testid={LOGIN.submitButton} disabled={busy} className="btn-primary w-full">{t("auth.login")}</button>
      </form>
      <Link data-testid="forgot-password-link" to="/forgot-password" className="block text-sm text-center text-[#2563EB]">{t("auth.forgot")}</Link>
      <p className="text-sm text-center muted">{t("auth.no_account")} <Link data-testid={LOGIN.registerLink} to="/register" className="text-[#2563EB] font-semibold">{t("auth.register")}</Link></p>
    </Shell>
  );
}

export function Register() {
  const { t } = useI18n();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/register", f); const me = await api.get("/auth/me"); setUser(me.data); nav("/account"); }
    catch (e2) { toast.error(t(`errors.${errCode(e2)}`)); }
  };
  return (
    <Shell title={t("auth.register_title")} sub={t("auth.register_sub")}>
      <GoogleBtn />
      <form onSubmit={submit} className="space-y-4">
        <Field label={t("checkout.name")} testid={REGISTER.nameInput} required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Field label={t("checkout.email")} testid={REGISTER.emailInput} type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field label={t("auth.password")} testid={REGISTER.passwordInput} type="password" required minLength={8} autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <p className="text-xs muted">{t("auth.password_hint")}</p>
        <button data-testid={REGISTER.submitButton} className="btn-primary w-full">{t("auth.register")}</button>
      </form>
      <p className="text-sm text-center muted">{t("auth.have_account")} <Link data-testid={REGISTER.loginLink} to="/login" className="text-[#2563EB] font-semibold">{t("auth.login")}</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => { e.preventDefault(); try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (e2) { toast.error(t(`errors.${errCode(e2)}`)); } };
  return (
    <Shell title={t("auth.forgot_title")} sub={t("auth.forgot_sub")}>
      {sent ? <p data-testid="forgot-sent" className="text-sm text-[#16A34A]">{t("auth.forgot_sent")}</p> :
        <form onSubmit={submit} className="space-y-4">
          <Field label={t("checkout.email")} testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <button data-testid="forgot-submit-btn" className="btn-primary w-full">{t("auth.forgot_send")}</button>
        </form>}
    </Shell>
  );
}

export function ResetPassword() {
  const { t } = useI18n();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: sp.get("token"), password: pw }); toast.success(t("auth.reset_done")); nav("/login"); }
    catch (e2) { toast.error(t(`errors.${errCode(e2)}`)); }
  };
  return (
    <Shell title={t("auth.reset_title")} sub={t("auth.password_hint")}>
      <form onSubmit={submit} className="space-y-4">
        <Field label={t("security.new_password")} testid="reset-password-input" type="password" required minLength={8} autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
        <button data-testid="reset-submit-btn" className="btn-primary w-full">{t("auth.reset_btn")}</button>
      </form>
    </Shell>
  );
}
